import { useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useDynamicConfig } from "@statsig/react-bindings";
import { useMutation } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { AdsManagerOnboardingPublicLandingPageView } from "@oai/analytics/gen/client-events/protobuf_analytics_events/v1/tapestry";
import { Button } from "@oai/platform/ui/Button";
import { ArrowUpRight, Globe, Pencil } from "@oai/platform/ui/Icon";
import { Input } from "@oai/platform/ui/Input";

import AdsManagerBrand from "@/components/common/AdsManagerBrand";
import SafeExternalLink from "@/components/common/SafeExternalLink";
import {
  ONBOARDING_BUSINESS_NAME_MAX_LENGTH,
  ONBOARDING_BUSINESS_NAME_MIN_LENGTH,
} from "@/components/onboarding/OnboardingFormTypes";
import { APIUrlContext } from "@/lib/apiUrlContext";
import {
  buildCarpetLiteDestinationUrl,
  CarpetLiteWebsiteCrawlingDisallowedError,
  CarpetLiteWebsiteCrawlingUnavailableError,
  clearStoredCarpetLiteCampaignContinuation,
  generateCarpetLiteCampaign,
  getCarpetLiteAdImageUrl,
  getCarpetLiteLogoCandidateUrl,
  regenerateCarpetLiteCampaignImage,
  storeCarpetLiteCampaignContinuation,
} from "@/lib/carpetLiteCampaign";
import { getUtmParameters, useClientTelemetry } from "@/lib/clientTelemetry";
import { PUBLIC_SOFA_BACKEND_URL } from "@/lib/constants";
import { getGeneratedImageUrl } from "@/lib/generatedRecommendationAssets";
import { normalizePublicWebsiteUrl } from "@/lib/websiteUrl";
import { messages } from "@/localization/CarpetLite.messages";
import { getPreviewableFaviconUrlInput } from "@/routes/adManager/AccountInfoFaviconUtils";
import { AdReviewCreativePreview } from "@/routes/adManager/components/AdReviewCreativePreview";
import {
  BAZAAR_GEO_TARGETING_COUNTRIES_CONFIG,
  DEFAULT_GEO_TARGETING_COUNTRY,
  getAllowedGeoTargetingCountries,
  getGeoTargetingCountryOptions,
} from "@/routes/adManager/GeoTargetingCountries";

import { CarpetLiteEditAdDialog, CarpetLiteEditCampaignDialog } from "./CarpetLiteEditDialogs";
import styles from "./CarpetLiteOrganicExperience.module.css";

import type { MouseEvent as ReactMouseEvent, RefObject } from "react";
import type {
  CarpetLiteCampaignPreview,
  CarpetLiteCampaignSettings,
} from "@/lib/carpetLiteCampaign";
import type { CarpetLiteEditableCreative, CarpetLiteUploadedLogo } from "./CarpetLiteEditDialogs";

const ADS_IN_CHATGPT_HELP_URL = "https://help.openai.com/en/articles/20001047-ads-in-chatgpt";
const ADVERTISER_CRAWLER_GUIDANCE_URL =
  "https://help.openai.com/en/articles/20001243-advertiser-guidance-for-allowing-openai-web-crawlers";
const ADVERTISING_TOOL_TERMS_URL = "https://openai.com/policies/ad-tools-terms/";
const THINKING_DETAILS_DELAY_MS = 2_400;
const MEGAPHONE_BACK_IMAGE =
  "data:image/svg+xml;base64,PHN2ZyBwcmVzZXJ2ZUFzcGVjdFJhdGlvPSJub25lIiBvdmVyZmxvdz0idmlzaWJsZSIgc3R5bGU9ImRpc3BsYXk6IGJsb2NrOyIgd2lkdGg9IjkuMzM0NjQiIGhlaWdodD0iMTEuNjY2NyIgdmlld0JveD0iMCAwIDkuMzM0NjQgMTEuNjY2NyIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggaWQ9InZlY3RvciIgZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik05LjMzNDY0IDExLjY2NjdINC4wMDEzQzEuNzkyMjIgMTEuNjY2NyA5LjI3NTUzZS0wNSA5Ljg3NTczIDAgNy42NjY2N1Y0QzguODAyMzZlLTA1IDEuNzkwOTQgMS43OTIyMiAwIDQuMDAxMyAwSDkuMzM0NjRWMTEuNjY2N1oiIGZpbGw9IiMyRTlFRkYiLz4KPC9zdmc+Cg==";
const MEGAPHONE_HANDLE_IMAGE =
  "data:image/svg+xml;base64,PHN2ZyBwcmVzZXJ2ZUFzcGVjdFJhdGlvPSJub25lIiBvdmVyZmxvdz0idmlzaWJsZSIgc3R5bGU9ImRpc3BsYXk6IGJsb2NrOyIgd2lkdGg9IjkuMzMzMzMiIGhlaWdodD0iOC42NjY2NyIgdmlld0JveD0iMCAwIDkuMzMzMzMgOC42NjY2NyIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggaWQ9InZlY3RvciIgZD0iTTAgNFYwSDIuNjY2NjdWNEMyLjY2NjY3IDQuNzM0OTMgMi44ODMyOSA1LjIwNTc2IDMuMTU2MjUgNS40ODY5OEMzLjQyMzIzIDUuNzYxODEgMy44ODQgNiA0LjY2NjY3IDZDNS40NDkzMyA2IDUuOTEwMTEgNS43NjE4MSA2LjE3NzA4IDUuNDg2OThDNi40NTAwNCA1LjIwNTc2IDYuNjY2NjcgNC43MzQ5MyA2LjY2NjY3IDRWMkg5LjMzMzMzVjRDOS4zMzMzMyA1LjI2NDczIDguOTUwMDQgNi40NjA4NiA4LjA4OTg0IDcuMzQ2MzVDNy4yMjM1MSA4LjIzODAxIDYuMDE3MiA4LjY2NjY3IDQuNjY2NjcgOC42NjY2N0MzLjMxNjEzIDguNjY2NjcgMi4xMDk4MyA4LjIzODAxIDEuMjQzNDkgNy4zNDYzNUMwLjM4MzI5NCA2LjQ2MDg2IDAgNS4yNjQ3MyAwIDRaIiBmaWxsPSIjMEM3OUQ4Ii8+Cjwvc3ZnPgo=";
const MEGAPHONE_CONE_IMAGE =
  "data:image/svg+xml;base64,PHN2ZyBwcmVzZXJ2ZUFzcGVjdFJhdGlvPSJub25lIiBvdmVyZmxvdz0idmlzaWJsZSIgc3R5bGU9ImRpc3BsYXk6IGJsb2NrOyIgd2lkdGg9IjE5LjMzMzMiIGhlaWdodD0iMjEuODA2OSIgdmlld0JveD0iMCAwIDE5LjMzMzMgMjEuODA2OSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggaWQ9InZlY3RvciIgZD0iTTEzLjk0NCAwLjI1MjQyQzE2LjU1NjIgLTAuNzE0NjU0IDE5LjMzMjkgMS4yMTcwMSAxOS4zMzMzIDQuMDAyNDJWMTcuODA0NUMxOS4zMzI5IDIwLjU4OTkgMTYuNTU2MSAyMi41MjE2IDEzLjk0NCAyMS41NTQ1TDAgMTYuNzMxNlY1LjA2NjA2TDEzLjk0NCAwLjI1MjQyWiIgZmlsbD0iIzY4QzRGRiIvPgo8L3N2Zz4K";

function getWebsiteHostname(websiteUrl: string): string {
  try {
    return new URL(normalizePublicWebsiteUrl(websiteUrl)).hostname.replace(/^www\./, "");
  } catch {
    return websiteUrl;
  }
}

function getWebsiteDisplayUrl(websiteUrl: string): string {
  try {
    const url = new URL(normalizePublicWebsiteUrl(websiteUrl));
    return `${url.hostname}${url.pathname === "/" ? "" : url.pathname}${url.search}${url.hash}`;
  } catch {
    return websiteUrl;
  }
}

function getWebsiteBrandName(hostname: string): string {
  const hostnameLabels = hostname.split(".");
  const hasCountryCodeDomain =
    hostnameLabels.length >= 3 &&
    hostnameLabels.at(-1)?.length === 2 &&
    ["ac", "co", "com", "edu", "gov", "net", "org"].includes(hostnameLabels.at(-2) ?? "");
  const domainLabel =
    hostnameLabels.at(hasCountryCodeDomain ? -3 : -2) ?? hostnameLabels[0] ?? hostname;
  const brandName = domainLabel
    .replace(/[-_]+/g, " ")
    .replace(/\b[a-z]/g, (character) => character.toUpperCase())
    .trim();
  return brandName.length >= ONBOARDING_BUSINESS_NAME_MIN_LENGTH ? brandName : hostname;
}

function EditableAdPreview({
  advertiserName,
  body,
  editLabel,
  imageUrl,
  logoUrl,
  title,
  editButtonRef,
  onEdit,
}: {
  advertiserName: string;
  body: string;
  editLabel: string;
  imageUrl: string | null;
  logoUrl: string | null;
  title: string;
  editButtonRef: RefObject<HTMLButtonElement | null>;
  onEdit: () => void;
}) {
  const descriptionId = useId();
  return (
    <button
      ref={editButtonRef}
      aria-label={editLabel}
      aria-describedby={descriptionId}
      className={styles.editableAdPreview}
      onClick={onEdit}
      type="button"
    >
      <div className={styles.adPreview}>
        <AdReviewCreativePreview
          advertiserName={advertiserName}
          body={body}
          imageUrl={imageUrl}
          previewImageUrl={logoUrl}
          title={title}
        />
      </div>
      <span className="sr-only" id={descriptionId}>
        {title}. {body}
      </span>
      <span aria-hidden="true" className={styles.editAdButton}>
        <Pencil aria-hidden="true" className="size-4" />
      </span>
    </button>
  );
}

type CarpetLiteOrganicExperienceProps = {
  initialWebsiteUrl?: string;
  accessToken?: string;
  onContinue?: (websiteUrl: string) => void;
  onSkip?: () => void;
};

export function CarpetLiteOrganicExperience({
  initialWebsiteUrl,
  accessToken,
  onContinue,
  onSkip,
}: CarpetLiteOrganicExperienceProps) {
  const intl = useIntl();
  const { logEvent, logStructuredEvent } = useClientTelemetry();
  const apiUrl = useContext(APIUrlContext) || PUBLIC_SOFA_BACKEND_URL;
  const geoTargetingCountriesConfig = useDynamicConfig(BAZAAR_GEO_TARGETING_COUNTRIES_CONFIG);
  const [websiteUrl, setWebsiteUrl] = useState(initialWebsiteUrl ?? "");
  const [showThinkingDetails, setShowThinkingDetails] = useState(false);
  const [showReviewDetails, setShowReviewDetails] = useState(false);
  const [continuationError, setContinuationError] = useState(false);
  const [isEditAdOpen, setIsEditAdOpen] = useState(false);
  const [isEditCampaignOpen, setIsEditCampaignOpen] = useState(false);
  const [creativeEdits, setCreativeEdits] = useState<CarpetLiteEditableCreative | null>(null);
  const [advertiserNameOverride, setAdvertiserNameOverride] = useState<string | null>(null);
  const [savedLogoOverride, setSavedLogoOverride] = useState<
    CarpetLiteUploadedLogo | null | undefined
  >();
  const [destinationUrlSuffix, setDestinationUrlSuffix] = useState("");
  const [regeneratedCampaign, setRegeneratedCampaign] = useState<CarpetLiteCampaignPreview | null>(
    null,
  );
  const [pendingRegeneratedCampaign, setPendingRegeneratedCampaign] =
    useState<CarpetLiteCampaignPreview | null>(null);
  const [campaignSettings, setCampaignSettings] = useState<CarpetLiteCampaignSettings>({
    objective: "clicks",
    locationCountryCodes: [DEFAULT_GEO_TARGETING_COUNTRY],
    budgetType: "daily",
    budgetAmount: "100",
    bidMode: "automated",
  });
  const websiteInputRef = useRef<HTMLInputElement>(null);
  const reviewHeadingRef = useRef<HTMLHeadingElement>(null);
  const shouldFocusReviewHeadingRef = useRef(false);
  const editAdButtonRef = useRef<HTMLButtonElement>(null);
  const editAdSessionRef = useRef(0);
  const editCampaignButtonRef = useRef<HTMLButtonElement>(null);
  const impressionLoggedRef = useRef(false);
  const autoGeneratedWebsiteRef = useRef<string | null>(null);
  const uploadedLogoObjectUrlsRef = useRef(new Set<string>());
  const generateCampaignMutation = useMutation({
    mutationFn: (url: string) =>
      generateCarpetLiteCampaign({ apiUrl, websiteUrl: url, accessToken }),
    onMutate: () => {
      setContinuationError(false);
      setShowThinkingDetails(false);
      setShowReviewDetails(false);
      setCreativeEdits(null);
      setAdvertiserNameOverride(null);
      setSavedLogoOverride(undefined);
      for (const objectUrl of uploadedLogoObjectUrlsRef.current) {
        URL.revokeObjectURL?.(objectUrl);
      }
      uploadedLogoObjectUrlsRef.current.clear();
      setDestinationUrlSuffix("");
      setRegeneratedCampaign(null);
      setPendingRegeneratedCampaign(null);
      editAdSessionRef.current += 1;
      setIsEditAdOpen(false);
      setIsEditCampaignOpen(false);
      logEvent("bazaar.tapestry.organic_campaign_preview.generation_started");
    },
    onSuccess: () => logEvent("bazaar.tapestry.organic_campaign_preview.generation_succeeded"),
    onError: () => logEvent("bazaar.tapestry.organic_campaign_preview.generation_failed"),
  });
  const { mutate: generateCampaign } = generateCampaignMutation;
  const generatedCampaign = regeneratedCampaign ?? generateCampaignMutation.data;
  const regenerationSourceCampaign = pendingRegeneratedCampaign ?? generatedCampaign;
  const regenerateImageMutation = useMutation({
    mutationFn: async () => {
      if (regenerationSourceCampaign == null) {
        throw new Error("Campaign preview is unavailable");
      }
      return regenerateCarpetLiteCampaignImage({
        apiUrl,
        websiteUrl: regenerationSourceCampaign.campaign.landing_page_url ?? websiteUrl,
        draftId: regenerationSourceCampaign.draft_id,
        accessToken,
      });
    },
    onSuccess: () => logEvent("bazaar.tapestry.organic_campaign_preview.image_regenerated"),
    onError: () => logEvent("bazaar.tapestry.organic_campaign_preview.image_regeneration_failed"),
  });
  const allowedCountryCodes = useMemo(
    () =>
      getAllowedGeoTargetingCountries(
        geoTargetingCountriesConfig.get("countries", [DEFAULT_GEO_TARGETING_COUNTRY]),
      ),
    [geoTargetingCountriesConfig],
  );
  const countryOptions = useMemo(
    () => getGeoTargetingCountryOptions(allowedCountryCodes, intl),
    [allowedCountryCodes, intl],
  );
  const selectedCountryCode =
    campaignSettings.locationCountryCodes[0] ?? DEFAULT_GEO_TARGETING_COUNTRY;
  const isBudgetValid =
    /^\d+(?:\.\d{1,2})?$/.test(campaignSettings.budgetAmount) &&
    Number(campaignSettings.budgetAmount) > 0;

  useEffect(() => {
    const uploadedLogoObjectUrls = uploadedLogoObjectUrlsRef.current;
    return () => {
      editAdSessionRef.current += 1;
      for (const objectUrl of uploadedLogoObjectUrls) {
        URL.revokeObjectURL?.(objectUrl);
      }
    };
  }, []);

  useEffect(() => {
    if (!generateCampaignMutation.isPending) {
      return;
    }
    const timeout = window.setTimeout(
      () => setShowThinkingDetails(true),
      THINKING_DETAILS_DELAY_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [generateCampaignMutation.isPending]);

  useEffect(() => {
    if (impressionLoggedRef.current) {
      return;
    }
    impressionLoggedRef.current = true;
    logEvent("bazaar.tapestry.onboarding_public_landing_page_view");
    logEvent("bazaar.tapestry.organic_campaign_preview.impression");
    logStructuredEvent(AdsManagerOnboardingPublicLandingPageView, {
      ...getUtmParameters(window.location.search),
      referrerUrl: document.referrer || undefined,
    });
  }, [logEvent, logStructuredEvent]);

  useEffect(() => {
    if (!initialWebsiteUrl) {
      return;
    }
    let normalizedWebsiteUrl: string;
    try {
      normalizedWebsiteUrl = normalizePublicWebsiteUrl(initialWebsiteUrl);
    } catch {
      return;
    }
    if (autoGeneratedWebsiteRef.current === normalizedWebsiteUrl) {
      return;
    }
    autoGeneratedWebsiteRef.current = normalizedWebsiteUrl;
    generateCampaign(normalizedWebsiteUrl);
  }, [generateCampaign, initialWebsiteUrl]);

  const firstGeneratedAdGroup = generatedCampaign?.campaign.ad_groups[0];
  const firstGeneratedAd = firstGeneratedAdGroup?.ads[0];
  const generatedImageUrl = firstGeneratedAd ? getCarpetLiteAdImageUrl(firstGeneratedAd) : null;
  const logoCandidateUrl = getCarpetLiteLogoCandidateUrl(generatedCampaign);
  const pendingRegeneratedAd = pendingRegeneratedCampaign?.campaign.ad_groups[0]?.ads[0];
  const editDialogImageUrl =
    pendingRegeneratedAd ? getCarpetLiteAdImageUrl(pendingRegeneratedAd) : generatedImageUrl;
  const imageUrl = generatedImageUrl;
  const displayedCreative =
    firstGeneratedAd ?
      {
        body: creativeEdits?.body ?? firstGeneratedAd.creative.body,
        title: creativeEdits?.title ?? firstGeneratedAd.creative.title,
      }
    : null;
  const websiteHostname = getWebsiteHostname(websiteUrl);
  const generatedAdvertiserName =
    generatedCampaign?.campaign.name.replace(/\s+campaign$/i, "").trim() ?? "";
  const hasGeneratedBrandName =
    generatedAdvertiserName.length >= ONBOARDING_BUSINESS_NAME_MIN_LENGTH &&
    getWebsiteHostname(generatedAdvertiserName).toLowerCase() !== websiteHostname.toLowerCase();
  const advertiserName = (
    advertiserNameOverride ??
    (hasGeneratedBrandName ? generatedAdvertiserName : getWebsiteBrandName(websiteHostname))
  ).slice(0, ONBOARDING_BUSINESS_NAME_MAX_LENGTH);
  const displayedLogoUrl =
    savedLogoOverride === undefined ? logoCandidateUrl : savedLogoOverride?.previewUrl ?? null;
  const generatedDestinationUrl =
    firstGeneratedAd?.creative.target_url ??
    generatedCampaign?.campaign.landing_page_url ??
    websiteUrl;
  const destinationUrl =
    buildCarpetLiteDestinationUrl(generatedDestinationUrl, destinationUrlSuffix) ??
    generatedDestinationUrl;
  const destinationDisplayUrl = getWebsiteDisplayUrl(destinationUrl);
  const contextHints = firstGeneratedAdGroup?.context_hints.filter((hint) => hint.trim()) ?? [];
  const readyDescription =
    contextHints.length >= 2 ?
      intl.formatMessage(messages.readyDescriptionWithTwoThemes, {
        firstTheme: contextHints[0],
        secondTheme: contextHints[1],
      })
    : contextHints.length === 1 ?
      intl.formatMessage(messages.readyDescriptionWithOneTheme, { theme: contextHints[0] })
    : intl.formatMessage(messages.readyDescription, {
        businessName: advertiserName,
      });
  const crawlingError =
    generateCampaignMutation.error instanceof CarpetLiteWebsiteCrawlingDisallowedError ||
    generateCampaignMutation.error instanceof CarpetLiteWebsiteCrawlingUnavailableError;

  const continueToOnboarding = () => {
    if (!generatedCampaign || !onContinue) {
      return;
    }
    try {
      const generatedCreative = firstGeneratedAd?.creative;
      const creativeOverride =
        (
          displayedCreative != null &&
          generatedCreative != null &&
          (displayedCreative.title !== generatedCreative.title ||
            displayedCreative.body !== generatedCreative.body ||
            destinationUrl !== generatedDestinationUrl)
        ) ?
          {
            title: displayedCreative.title,
            body: displayedCreative.body,
            ...(destinationUrl === generatedDestinationUrl ? {} : { targetUrl: destinationUrl }),
          }
        : undefined;
      const didStoreContinuation = storeCarpetLiteCampaignContinuation(
        generatedCampaign,
        campaignSettings,
        creativeOverride,
        {
          businessName: advertiserName,
          fallbackWebsiteUrl: websiteUrl,
          logoFileId: savedLogoOverride?.fileId,
          logoRemoved: savedLogoOverride === null,
        },
      );
      if (!didStoreContinuation) {
        setContinuationError(true);
        return;
      }
      setContinuationError(false);
      onContinue(
        normalizePublicWebsiteUrl(generatedCampaign.campaign.landing_page_url ?? websiteUrl),
      );
    } catch {
      return;
    }
  };

  useEffect(() => {
    if (showReviewDetails && shouldFocusReviewHeadingRef.current) {
      reviewHeadingRef.current?.focus();
      shouldFocusReviewHeadingRef.current = false;
    }
  }, [showReviewDetails]);

  const openReviewDetails = (event: ReactMouseEvent<HTMLButtonElement>) => {
    shouldFocusReviewHeadingRef.current = event.detail === 0;
    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (typeof document.startViewTransition !== "function" || prefersReducedMotion) {
      setShowReviewDetails(true);
      return;
    }
    document.startViewTransition(() => {
      flushSync(() => setShowReviewDetails(true));
    });
  };

  const closeEditAd = (keepPreviewUrl = savedLogoOverride?.previewUrl) => {
    editAdSessionRef.current += 1;
    for (const objectUrl of uploadedLogoObjectUrlsRef.current) {
      if (objectUrl !== keepPreviewUrl) {
        URL.revokeObjectURL?.(objectUrl);
        uploadedLogoObjectUrlsRef.current.delete(objectUrl);
      }
    }
    setPendingRegeneratedCampaign(null);
    setIsEditAdOpen(false);
    window.setTimeout(() => editAdButtonRef.current?.focus(), 0);
  };

  const openEditAd = () => {
    editAdSessionRef.current += 1;
    setPendingRegeneratedCampaign(null);
    setIsEditAdOpen(true);
  };

  const closeEditCampaign = () => {
    setIsEditCampaignOpen(false);
    window.setTimeout(() => editCampaignButtonRef.current?.focus(), 0);
  };

  return (
    <section className={styles.experience}>
      <header className={styles.header}>
        <AdsManagerBrand label={intl.formatMessage(messages.adsManager)} textClassName="text-md" />
        {onSkip ?
          <Button
            color="secondary"
            onClick={() => {
              clearStoredCarpetLiteCampaignContinuation();
              onSkip();
            }}
            pill
            size="md"
            variant="outline"
          >
            {intl.formatMessage(messages.skipToAdsManager)}
          </Button>
        : null}
      </header>

      <div aria-busy={generateCampaignMutation.isPending} className={styles.content}>
        {generateCampaignMutation.isPending ?
          <div aria-live="polite" className={styles.thinking} role="status">
            <span className={styles.websiteBadge}>
              <span aria-hidden="true" className={styles.websiteIcon}>
                <Globe className="size-3" />
              </span>
              {websiteUrl}
            </span>
            <p className={styles.thinkingLabel}>{intl.formatMessage(messages.thinking)}</p>
            <p className={styles.thinkingDescription}>
              {intl.formatMessage(
                showThinkingDetails ?
                  messages.thinkingDetailsDescription
                : messages.thinkingDescription,
                { website: websiteHostname },
              )}
            </p>
          </div>
        : firstGeneratedAd && displayedCreative && generatedCampaign && showReviewDetails ?
          <div className={styles.review}>
            <h1 className={styles.heading} ref={reviewHeadingRef} tabIndex={-1}>
              {intl.formatMessage(messages.reviewHeading)}
            </h1>
            <div className={styles.reviewPreview}>
              <EditableAdPreview
                advertiserName={advertiserName}
                body={displayedCreative.body}
                editButtonRef={editAdButtonRef}
                editLabel={intl.formatMessage(messages.editAd)}
                imageUrl={imageUrl}
                logoUrl={displayedLogoUrl}
                onEdit={openEditAd}
                title={displayedCreative.title}
              />
              <div className={styles.reviewSummary}>
                <p className={styles.reviewDescription}>
                  {intl.formatMessage(messages.reviewDescription)}
                </p>
                <div className={styles.reviewDetailsSection}>
                  <button
                    aria-label={intl.formatMessage(messages.editCampaignDetails)}
                    className={styles.editDetailsButton}
                    onClick={() => setIsEditCampaignOpen(true)}
                    ref={editCampaignButtonRef}
                    type="button"
                  >
                    <Pencil aria-hidden="true" className="size-4" />
                    <span>{intl.formatMessage(messages.editRecommendedPlan)}</span>
                  </button>
                  <div className={styles.reviewDetails}>
                    <div className={styles.reviewDetailsRow}>
                      <div className={styles.reviewDetail}>
                        <span className={styles.reviewDetailLabel}>
                          {intl.formatMessage(messages.advertiserName)}
                        </span>
                        <span className={styles.reviewDetailValue}>{advertiserName}</span>
                      </div>
                      <div className={styles.reviewDetail}>
                        <span className={styles.reviewDetailLabel}>
                          {intl.formatMessage(messages.objective)}
                        </span>
                        <span className={styles.reviewDetailValue}>
                          {intl.formatMessage(
                            campaignSettings.objective === "clicks" ?
                              messages.clicks
                            : messages.reach,
                          )}
                        </span>
                      </div>
                    </div>
                    <div className={styles.reviewDetailsRow}>
                      <div className={styles.reviewDetail}>
                        <span className={styles.reviewDetailLabel}>
                          {intl.formatMessage(messages.locations)}
                        </span>
                        <span className={styles.reviewDetailValue}>
                          {countryOptions.find((option) => option.value === selectedCountryCode)
                            ?.label ?? selectedCountryCode}
                        </span>
                      </div>
                      <div className={styles.reviewDetail}>
                        <span className={styles.reviewDetailLabel}>
                          {intl.formatMessage(messages.destinationUrl)}
                        </span>
                        <span className={styles.reviewDetailValue}>{destinationDisplayUrl}</span>
                      </div>
                    </div>
                    <div className={styles.reviewDetailsRow}>
                      <div className={styles.reviewDetail}>
                        <span className={styles.reviewDetailLabel}>
                          {intl.formatMessage(messages.recommendedBidStrategy)}
                        </span>
                        <span className={styles.reviewDetailValue}>
                          {intl.formatMessage(
                            campaignSettings.bidMode === "automated" ?
                              messages.maximumResults
                            : messages.manualBid,
                          )}
                        </span>
                      </div>
                      <div className={styles.reviewDetail}>
                        <span className={styles.reviewDetailLabel}>
                          {intl.formatMessage(
                            campaignSettings.budgetType === "daily" ?
                              messages.averageDailyBudget
                            : messages.campaignTotalBudget,
                          )}
                        </span>
                        <span className={styles.reviewDetailValue}>
                          {intl.formatNumber(Number(campaignSettings.budgetAmount), {
                            currency: "USD",
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 0,
                            style: "currency",
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.reviewActions}>
              <Button
                key="launch-campaign"
                className={styles.primaryAction}
                color="primary"
                disabled={!isBudgetValid}
                onClick={continueToOnboarding}
                pill
                size="xl"
              >
                {intl.formatMessage(messages.launchCampaign)}
              </Button>
              <p className={styles.termsNotice}>
                <FormattedMessage
                  {...messages.launchTerms}
                  values={{
                    termsLink: (chunks) => (
                      <SafeExternalLink
                        className={styles.termsLink}
                        href={ADVERTISING_TOOL_TERMS_URL}
                      >
                        {chunks}
                      </SafeExternalLink>
                    ),
                  }}
                />
              </p>
            </div>
            {continuationError ?
              <p className="text-danger text-center text-sm" role="alert">
                {intl.formatMessage(messages.continuationError)}
              </p>
            : null}
          </div>
        : firstGeneratedAd && displayedCreative && generatedCampaign ?
          <div className={styles.ready}>
            <h1 className={styles.heading}>{intl.formatMessage(messages.readyHeading)}</h1>
            <p className={styles.readyDescription}>{readyDescription}</p>
            <EditableAdPreview
              advertiserName={advertiserName}
              body={displayedCreative.body}
              editButtonRef={editAdButtonRef}
              editLabel={intl.formatMessage(messages.editAd)}
              imageUrl={imageUrl}
              logoUrl={displayedLogoUrl}
              onEdit={openEditAd}
              title={displayedCreative.title}
            />
            <div className={styles.readyActions}>
              <Button
                key="review-details"
                className={styles.primaryAction}
                color="primary"
                onClick={openReviewDetails}
                pill
                size="xl"
              >
                {intl.formatMessage(messages.reviewDetails)}
              </Button>
            </div>
          </div>
        : <div className={styles.entry}>
            <div aria-hidden="true" className={styles.introIcon}>
              <div className={styles.megaphoneIcon}>
                <img alt="" className={styles.megaphoneBack} src={MEGAPHONE_BACK_IMAGE} />
                <img alt="" className={styles.megaphoneHandle} src={MEGAPHONE_HANDLE_IMAGE} />
                <img alt="" className={styles.megaphoneCone} src={MEGAPHONE_CONE_IMAGE} />
              </div>
            </div>
            <div className="flex flex-col items-center gap-2">
              <h1 className={styles.heading}>{intl.formatMessage(messages.heading)}</h1>
              <p className={styles.description}>{intl.formatMessage(messages.description)}</p>
            </div>
            <form
              className={styles.actions}
              onSubmit={(event) => {
                event.preventDefault();
                generateCampaign(websiteUrl);
              }}
            >
              <Input
                aria-label={intl.formatMessage(messages.websiteLabel)}
                inputMode="url"
                maxLength={2048}
                onAutofill={() => setWebsiteUrl(websiteInputRef.current?.value ?? "")}
                onChange={(event) => setWebsiteUrl(event.currentTarget.value)}
                onFocus={(event) => setWebsiteUrl(event.currentTarget.value)}
                pill
                placeholder={intl.formatMessage(messages.websitePlaceholder)}
                ref={websiteInputRef}
                size="xl"
                startAdornment={<Globe aria-hidden="true" className="size-4" />}
                value={websiteUrl}
              />
              <Button
                className={styles.primaryAction}
                color="primary"
                disabled={!websiteUrl.trim()}
                pill
                size="xl"
                type="submit"
              >
                {intl.formatMessage(messages.generate)}
              </Button>
              <SafeExternalLink className={styles.learnMore} href={ADS_IN_CHATGPT_HELP_URL}>
                {intl.formatMessage(messages.learnMore)}
                <ArrowUpRight aria-hidden="true" className={styles.learnMoreIcon} />
              </SafeExternalLink>
            </form>
            {generateCampaignMutation.isError ?
              <div className="text-danger flex flex-col items-center gap-2 text-sm" role="alert">
                <p className="m-0">
                  {intl.formatMessage(
                    (
                      generateCampaignMutation.error instanceof
                        CarpetLiteWebsiteCrawlingDisallowedError
                    ) ?
                      messages.crawlingDisallowedError
                    : (
                      generateCampaignMutation.error instanceof
                      CarpetLiteWebsiteCrawlingUnavailableError
                    ) ?
                      messages.crawlingUnavailableError
                    : messages.genericError,
                  )}
                </p>
                {crawlingError ?
                  <SafeExternalLink
                    className="underline underline-offset-2"
                    href={ADVERTISER_CRAWLER_GUIDANCE_URL}
                  >
                    {intl.formatMessage(messages.crawlingGuidanceLink)}
                  </SafeExternalLink>
                : null}
              </div>
            : null}
          </div>
        }
      </div>

      {firstGeneratedAd && displayedCreative && generatedCampaign ?
        <>
          <CarpetLiteEditAdDialog
            advertiserName={advertiserName}
            creative={displayedCreative}
            generatedImageUrl={editDialogImageUrl}
            isRegeneratingImage={regenerateImageMutation.isPending}
            logoUrl={displayedLogoUrl}
            onClose={closeEditAd}
            onRegenerateImage={async () => {
              const editSession = editAdSessionRef.current;
              try {
                const preview = await regenerateImageMutation.mutateAsync();
                if (editAdSessionRef.current !== editSession) {
                  return false;
                }
                setPendingRegeneratedCampaign(preview);
                return true;
              } catch {
                return false;
              }
            }}
            onSave={(creative, nextAdvertiserName, logoOverride) => {
              if (pendingRegeneratedCampaign != null) {
                setRegeneratedCampaign(pendingRegeneratedCampaign);
              }
              setCreativeEdits(creative);
              setAdvertiserNameOverride(nextAdvertiserName);
              if (logoOverride !== undefined) {
                setSavedLogoOverride(logoOverride);
              }
              closeEditAd(
                logoOverride === undefined ?
                  savedLogoOverride?.previewUrl
                : logoOverride?.previewUrl,
              );
            }}
            onUploadLogo={async (source) => {
              const editSession = editAdSessionRef.current;
              if (!accessToken) {
                throw new Error("Invalid advertiser logo");
              }
              let headers: Record<string, string> = {
                authorization: `Bearer ${accessToken}`,
              };
              let body: FormData | string;
              let sourceImageUrl: string | undefined;
              if (typeof source === "string") {
                const trimmedSource = source.trim();
                const normalizedSource = normalizePublicWebsiteUrl(trimmedSource);
                sourceImageUrl = getGeneratedImageUrl(
                  /^https?:\/\//i.test(trimmedSource) ? trimmedSource : normalizedSource,
                );
                if (!sourceImageUrl) {
                  throw new Error("Invalid advertiser logo");
                }
                headers = { ...headers, "content-type": "application/json" };
                body = JSON.stringify({ image_url: sourceImageUrl, purpose: "account_favicon" });
              } else {
                if (
                  !["image/gif", "image/jpeg", "image/jpg", "image/png", "image/webp"].includes(
                    source.type,
                  ) ||
                  source.size > 10 * 1024 * 1024
                ) {
                  throw new Error("Invalid advertiser logo");
                }
                const formData = new FormData();
                formData.append("file", source);
                formData.append("purpose", "account_favicon");
                body = formData;
              }
              const response = await fetch(`${apiUrl.replace(/\/$/, "")}/v1/ui/onboarding/upload`, {
                method: "POST",
                headers,
                body,
              });
              if (!response.ok) {
                throw new Error("Advertiser logo upload failed");
              }
              const result = (await response.json()) as { file_id?: unknown };
              if (
                typeof result.file_id !== "string" ||
                result.file_id.trim().length === 0 ||
                result.file_id.length > 255
              ) {
                throw new Error("Advertiser logo upload failed");
              }
              if (editAdSessionRef.current !== editSession) {
                throw new Error("Advertiser logo upload was discarded");
              }
              const previewUrl =
                typeof source === "string" ?
                  getPreviewableFaviconUrlInput(sourceImageUrl ?? "") ?? sourceImageUrl ?? ""
                : URL.createObjectURL(source);
              if (typeof source !== "string") {
                uploadedLogoObjectUrlsRef.current.add(previewUrl);
              }
              return { fileId: result.file_id.trim(), previewUrl };
            }}
            open={isEditAdOpen}
            websiteUrl={generatedCampaign.campaign.landing_page_url ?? websiteUrl}
          />
          <CarpetLiteEditCampaignDialog
            countryOptions={countryOptions}
            destinationUrl={generatedDestinationUrl}
            destinationUrlSuffix={destinationUrlSuffix}
            onClose={closeEditCampaign}
            onSave={(settings, nextDestinationUrlSuffix) => {
              setCampaignSettings(settings);
              setDestinationUrlSuffix(nextDestinationUrlSuffix);
              closeEditCampaign();
            }}
            open={isEditCampaignOpen}
            settings={campaignSettings}
          />
        </>
      : null}
    </section>
  );
}
